import { useTranslation } from "react-i18next";
import {
    Alert,
    Box,
    CircularProgress,
    IconButton,
    Paper,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import { Icon } from "@tribe/react-mui-overheid-thema";
import { useState } from "react";
import { usePermissions } from "@/src/lib/usePermissions/usePermissions";

type SubjectDid = {
    id: string;
    name: string;
    zinlDid: string;
    healthcareOfficeIri?: string | null;
};

interface SubjectDidsTableProps {
    subjectDids: SubjectDid[];
    loading: boolean;
    onDeleteClick?: (id: string) => void;
    onItemClick?: (id: string) => void;
    page: number;
    rowsPerPage: number;
    totalCount: number;
    onPageChange: (event: unknown, newPage: number) => void;
    onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const SubjectDidsTable = ({
    subjectDids,
    loading,
    onDeleteClick,
    onItemClick,
    page,
    rowsPerPage,
    totalCount,
    onPageChange,
    onRowsPerPageChange,
}: SubjectDidsTableProps) => {
    const { t } = useTranslation("common", {
        keyPrefix: "subject-dids-overview",
    });

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarKey, setSnackbarKey] = useState(0);

    const { hasPermission } = usePermissions();

    const handleCopyDid = (did: string) => {
        if (!did) return;

        navigator.clipboard.writeText(did);
        setSnackbarKey((prev) => prev + 1);
        setSnackbarOpen(true);
    };

    const handleCloseSnackbar = (
        _event: React.SyntheticEvent | Event,
        reason?: string
    ) => {
        if (reason === "clickaway") {
            return;
        }

        setSnackbarOpen(false);
    };

    if (subjectDids.length === 0 && !loading) {
        return (
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
                {t("table.no-data")}
            </Typography>
        );
    }

    return (
        <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="providers table">
                <TableHead>
                    <TableRow>
                        <TableCell>{t("table.name")}</TableCell>
                        <TableCell>{t("table.did")}</TableCell>
                        <TableCell>{t("table.iri")}</TableCell>
                        <TableCell>{t("table.delete")}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loading ? (
                        <TableRow>
                            <TableCell colSpan={4} align="center">
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        p: 2,
                                    }}
                                >
                                    <CircularProgress />
                                </Box>
                            </TableCell>
                        </TableRow>
                    ) : (
                        subjectDids.map((subjectDid) => (
                            <TableRow
                                key={subjectDid.id}
                                onClick={() => onItemClick?.(subjectDid.id)}
                                sx={{
                                    "&:last-child td, &:last-child th": {
                                        border: 0,
                                    },
                                    ...(onItemClick && {
                                        cursor: "pointer",
                                        "&:hover": {
                                            backgroundColor: "action.hover",
                                        },
                                    }),
                                }}
                            >
                                <TableCell component="th" scope="row">
                                    {subjectDid.name}
                                </TableCell>
                                <TableCell
                                    sx={{
                                        maxWidth: 200,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        handleCopyDid(subjectDid.zinlDid);
                                    }}
                                >
                                    <Tooltip
                                        title={
                                            <>
                                                <Typography>
                                                    {t(
                                                        "table.click-to-copy-did"
                                                    )}
                                                </Typography>
                                                {subjectDid.zinlDid || "-"}
                                            </>
                                        }
                                    >
                                        <Box
                                            component="span"
                                            sx={{
                                                width: "100%",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {subjectDid.zinlDid || "-"}
                                        </Box>
                                    </Tooltip>
                                </TableCell>
                                <TableCell>
                                    {subjectDid.healthcareOfficeIri || "-"}
                                </TableCell>
                                <TableCell align="center">
                                    {hasPermission("SUBJECT_DIDS_DELETE") ? (
                                        <IconButton
                                            edge="end"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                onDeleteClick?.(subjectDid.id);
                                            }}
                                            aria-label={t("table.delete")}
                                            data-item-id={subjectDid.id}
                                        >
                                            <Icon name="verwijderen" />
                                        </IconButton>
                                    ) : (
                                        "-"
                                    )}
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
            <TablePagination
                rowsPerPageOptions={[5, 15, 25]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
            />
            <Snackbar
                key={snackbarKey}
                open={snackbarOpen}
                onClose={handleCloseSnackbar}
                autoHideDuration={3000}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity="success" onClose={handleCloseSnackbar}>
                    {t("table.did-copied")}
                </Alert>
            </Snackbar>
        </TableContainer>
    );
};

export default SubjectDidsTable;
